import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useDateFns, useLanguage } from '~/Hooks';
import { selectLoading } from '~/store/app';
import { selectNextEventsMap, selectSyncedEventGroupsMap } from '~/store/eventGroups';
import { CellTypes, Column } from '~/Components/DataTable/DataTable.types';
import { selectLocationsMap, selectTrainersMap } from '~/store/provider';
import { ITrainer } from '~/Models/DTO/Trainer.models';
import { TrainerList } from '../TrainerList';
import { ILocation } from '~/Models/DTO/Location.models';
import { EventGroup } from '~/Models/DTO/EventGroup.model';
import { Dots } from '~/UIKit';
import { REQUEST_ACTIONS } from '~/Constants/Actions.constants';
import { USCIntegrationStatus } from '~/Screens/EventGroupList/USCIntegrationStatus';

type IEventGroupColumnNameType =
  | 'name'
  | 'providerCategory'
  | 'nextEvent'
  | 'trainer'
  | 'location'
  | 'uscIntegration';

export const useEventGroupColumns = () => {
  const { t } = useLanguage();
  const { localeDateFormat, formatDate } = useDateFns();

  const isNextEventLoading = useSelector(selectLoading(REQUEST_ACTIONS.GET_NEXT_EVENTS));
  const isEventGroupsLoading = useSelector(selectLoading(REQUEST_ACTIONS.GET_EVENTGROUP_LIST));
  const isLocationLoading = useSelector(selectLoading(REQUEST_ACTIONS.LOCATION_LIST));
  const isTrainersLoading = useSelector(selectLoading(REQUEST_ACTIONS.TRAINER_LIST));
  const isSyncedEventGroupsLoading = useSelector(
    selectLoading(REQUEST_ACTIONS.GET_SYNCED_EVENT_GROUP_LIST),
  );

  const nextEventsMap = useSelector(selectNextEventsMap);
  const syncedEventGroupsMap = useSelector(selectSyncedEventGroupsMap);

  const trainersMap = useSelector(selectTrainersMap);
  const locationsMap = useSelector(selectLocationsMap);

  const columnMap: {
    [key in IEventGroupColumnNameType]: Column;
  } = useMemo(
    () => ({
      name: {
        label: t('form.field.name'),
        style: { width: '30%' },
        propertyIds: 'name',
        cellType: CellTypes.String,
        formatter: (value: any) => value,
      },
      providerCategory: {
        label: t('event.eventCategory'),
        propertyIds: 'categoryType',
        cellType: CellTypes.String,
        formatter: (value: string) => t(`event.category.${value}`),
      },
      nextEvent: {
        label: t('app.eventlist.nextevent'),
        propertyIds: 'id',
        cellType: CellTypes.String,
        formatter: (id: string) => {
          const nextEvent = nextEventsMap[id];
          if (isNextEventLoading) {
            return <Dots />;
          }
          if (!nextEvent) {
            return t('app.eventList.noNextEvent');
          }

          return formatDate(nextEvent.start, `${localeDateFormat} HH:mm`);
        },
      },
      trainer: {
        label: t('form.field.trainer'),
        propertyIds: 'trainerIds',
        cellType: CellTypes.String,
        formatter: (trainerIds: ITrainer['trainerId'][]) => (
          <TrainerList
            trainerIds={trainerIds}
            trainersMap={trainersMap}
            isTrainersLoading={isTrainersLoading}
          />
        ),
      },
      location: {
        label: t('form.field.location'),
        propertyIds: 'locationId',
        cellType: CellTypes.String,
        formatter: (locationId: ILocation['id']) => {
          if (isLocationLoading) {
            return <Dots />;
          }
          return locationsMap[locationId]?.name;
        },
      },
      uscIntegration: {
        label: t('integrations.usc.title.abbreviation'),
        propertyIds: 'id',
        cellType: CellTypes.String,
        style: { width: 85 },
        formatter: (id: EventGroup['id']) => (
          <USCIntegrationStatus
            isLoading={isSyncedEventGroupsLoading}
            isIntegrated={!!syncedEventGroupsMap[id]}
          />
        ),
      },
    }),
    [
      trainersMap,
      locationsMap,
      nextEventsMap,
      isLocationLoading,
      isTrainersLoading,
      isNextEventLoading,
      syncedEventGroupsMap,
      isEventGroupsLoading,
      isSyncedEventGroupsLoading,
      t,
    ],
  );

  return {
    columnMap,
  };
};
